import FEATURES from 'src/core/constants/features';

const spendingMethods = [
  FEATURES.EXPENSE_CLAIMS,
  FEATURES.SUBMIT_MY_INVOICE,
  FEATURES.PURCHASE_ORDERS_ACTIVATED,
  FEATURES.DELIVERY_NOTES_FEATURE_ACTIVATED,
  FEATURES.PER_DIEM_ALLOWANCES,
  FEATURES.MILEAGE_ALLOWANCES,
] as const;
export type SpendingMethod = (typeof spendingMethods)[number];

export function isSpendingMethodAllowed(
  spendingMethod: SpendingMethod,
  country: string,
  {
    isControlOnPurchaseOrderEnabled,
    isControlOnSubmitMyInvoice,
    isDeliveryNoteEnabled,
  }: {
    isControlOnPurchaseOrderEnabled: boolean;
    isControlOnSubmitMyInvoice: boolean;
    isDeliveryNoteEnabled: boolean;
  },
): boolean {
  switch (spendingMethod) {
    case FEATURES.SUBMIT_MY_INVOICE:
      return isControlOnSubmitMyInvoice;
    case FEATURES.PURCHASE_ORDERS_ACTIVATED:
      return isControlOnPurchaseOrderEnabled;
    case FEATURES.DELIVERY_NOTES_FEATURE_ACTIVATED:
      return isDeliveryNoteEnabled || isControlOnPurchaseOrderEnabled;
    case FEATURES.PER_DIEM_ALLOWANCES:
      return country === 'DE';
    default:
      return true;
  }
}

export function isSpendingMethodDisabled(
  featureCode: SpendingMethod,
  {
    isPurchaseOrderEnabled,
    isCostCenterActivated,
    hasMileageScheme,
    isInvoiceEnabled,
    isDeliveryNoteEnabled,
  }: {
    isPurchaseOrderEnabled: boolean;
    isCostCenterActivated: boolean;
    hasMileageScheme: boolean;
    isInvoiceEnabled: boolean;
    isDeliveryNoteEnabled: boolean;
  },
): boolean {
  switch (featureCode) {
    case FEATURES.SUBMIT_MY_INVOICE:
      return isPurchaseOrderEnabled;
    case FEATURES.PURCHASE_ORDERS_ACTIVATED:
      return !isCostCenterActivated || !isInvoiceEnabled;
    case FEATURES.DELIVERY_NOTES_FEATURE_ACTIVATED:
      // if isDeliveryNoteEnabled is false, we disable the toggle definitely,
      // if isDeliveryNoteEnabled is true, then it depends if purchase order is enabled or not
      return !(isDeliveryNoteEnabled && isPurchaseOrderEnabled);
    case FEATURES.MILEAGE_ALLOWANCES:
      return !hasMileageScheme;
    default:
      return false;
  }
}

type getAllowedSpendingMethodsResult = {
  featureCode: SpendingMethod;
  isDisabled: boolean;
}[];

export function getAllowedSpendingMethods(
  country: string,
  {
    isControlOnSubmitMyInvoice,
    isInvoiceEnabled,
    isControlOnPurchaseOrderEnabled,
    isPurchaseOrderEnabled,
    isDeliveryNoteEnabled,
    isCostCenterActivated,
    hasMileageScheme,
  }: {
    isControlOnSubmitMyInvoice: boolean;
    isInvoiceEnabled: boolean;
    isControlOnPurchaseOrderEnabled: boolean;
    isPurchaseOrderEnabled: boolean;
    isDeliveryNoteEnabled: boolean;
    isCostCenterActivated: boolean;
    hasMileageScheme: boolean;
  },
): getAllowedSpendingMethodsResult {
  return spendingMethods
    .filter((spendingMethod) =>
      isSpendingMethodAllowed(spendingMethod, country, {
        isControlOnSubmitMyInvoice,
        isControlOnPurchaseOrderEnabled,
        isDeliveryNoteEnabled,
      }),
    )
    .map((featureCode) => {
      return {
        featureCode,
        isDisabled: isSpendingMethodDisabled(featureCode, {
          isInvoiceEnabled,
          isPurchaseOrderEnabled,
          isCostCenterActivated,
          hasMileageScheme,
          isDeliveryNoteEnabled,
        }),
      };
    });
}
